import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { officeListRequests, officeUpdateRequest, officeEnsureCustomerForRequest, officeResendCustomerInvite } from "@/lib/office.functions";
import { Phone, Mail, MapPin, Clock, UserPlus, MailPlus, CheckCircle2 } from "lucide-react";

import { toast } from "sonner";
import { VoysCallButton } from "@/components/voys/VoysCallButton";

export const Route = createFileRoute("/_employee/medewerker/office/aanvragen")({
  head: () => ({
    meta: [{ title: "Aanvragen — EVG Office" }, { name: "robots", content: "noindex, nofollow" }],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/medewerker/office/aanvragen" }],
  }),
  component: RequestsPage,
});

const STAGES = [
  { id: "all", label: "Alle", color: "bg-slate-100 text-slate-700" },
  { id: "new", label: "Nieuw", color: "bg-amber-100 text-amber-700" },
  { id: "contacted", label: "Gecontacteerd", color: "bg-sky-100 text-sky-700" },
  { id: "qualified", label: "Gekwalificeerd", color: "bg-violet-100 text-violet-700" },
  { id: "quoted", label: "Offerte uit", color: "bg-blue-100 text-blue-700" },
  { id: "won", label: "Gewonnen", color: "bg-emerald-100 text-emerald-700" },
  { id: "lost", label: "Verloren", color: "bg-slate-200 text-slate-500" },
];

const LABELS: Record<string, { cls: string; text: string }> = {
  purple: { cls: "bg-violet-100 text-violet-700 ring-violet-200", text: "Geen offerte" },
  blue:   { cls: "bg-blue-100 text-blue-700 ring-blue-200",       text: "Offerte uit" },
  green:  { cls: "bg-emerald-100 text-emerald-700 ring-emerald-200", text: "Akkoord" },
  red:    { cls: "bg-rose-100 text-rose-700 ring-rose-200",       text: "Afgewezen" },
};

function StageBadge({ stage }: { stage: string }) {
  const s = STAGES.find((x) => x.id === stage) ?? STAGES[1];
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${s.color}`}>{s.label}</span>;
}

function StatusLabel({ label }: { label?: string }) {
  const l = LABELS[label ?? "purple"] ?? LABELS.purple;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${l.cls}`}>
      {l.text}
    </span>
  );
}

function RequestsPage() {
  const [stage, setStage] = useState("all");
  const [search, setSearch] = useState("");
  const list = useServerFn(officeListRequests);
  const update = useServerFn(officeUpdateRequest);
  const ensureCustomer = useServerFn(officeEnsureCustomerForRequest);
  const resendInvite = useServerFn(officeResendCustomerInvite);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["office-requests", stage, search],
    queryFn: () => list({ data: { stage, search } }),
  });

  const mut = useMutation({
    mutationFn: (v: any) => update({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["office-requests"] }),
  });

  const inviteMut = useMutation({
    mutationFn: (request_id: string) => ensureCustomer({ data: { request_id } }),
    onSuccess: (res: any) => {
      toast.success(res?.invited ? `Klant aangemaakt (${res.customer_number}) — uitnodiging verstuurd` : `Klant gekoppeld (${res.customer_number})`);
      qc.invalidateQueries({ queryKey: ["office-requests"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Kon klant niet aanmaken"),
  });

  const resendMut = useMutation({
    mutationFn: (request_id: string) => resendInvite({ data: { request_id } }),
    onSuccess: (res: any) => {
      if (res?.alreadyActivated) toast.info("Klant heeft account al geactiveerd");
      else toast.success("Uitnodigingsmail opnieuw verstuurd");
      qc.invalidateQueries({ queryKey: ["office-requests"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Kon uitnodiging niet versturen"),
  });


  async function setStageFor(id: string, newStage: string) {
    await mut.mutateAsync({ id, stage: newStage });
    toast.success("Bijgewerkt");
  }

  const requests = data?.requests ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Offerte-aanvragen</h1>
          <p className="text-sm text-slate-500">{requests.length} aanvragen — CRM pijplijn</p>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek naam, e-mail, tekst…"
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0b2a4a]"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STAGES.map((s) => (
          <button
            key={s.id}
            onClick={() => setStage(s.id)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
              stage === s.id ? "bg-[#0b2a4a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="p-3 text-left font-semibold">Klant</th>
              <th className="p-3 text-left font-semibold">Contact</th>
              <th className="p-3 text-left font-semibold">Dienst</th>
              <th className="p-3 text-left font-semibold">Offerte</th>
              <th className="p-3 text-left font-semibold">Bericht</th>
              <th className="p-3 text-left font-semibold">Fase</th>
              <th className="p-3 text-left font-semibold">Ontvangen</th>
              <th className="p-3 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">Laden…</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">Geen aanvragen</td></tr>
            ) : requests.map((r: any) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{r.name}</span>
                    <StatusLabel label={r.label} />
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {r.customer_number ? (
                      <>
                        <Link
                          to="/medewerker/office/klanten/$id"
                          params={{ id: r.customer_id }}
                          className="font-mono text-[11px] text-[#0b2a4a] underline-offset-2 hover:underline"
                          title="Open klantprofiel"
                        >
                          {r.customer_number}
                        </Link>
                        {r.customer_activated ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200" title="Klant heeft account geactiveerd">
                            <CheckCircle2 className="size-3" /> Actief
                          </span>
                        ) : (
                          <button
                            onClick={() => resendMut.mutate(r.id)}
                            disabled={resendMut.isPending}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100 disabled:opacity-50"
                            title="Uitnodigingsmail opnieuw versturen"
                          >
                            <MailPlus className="size-3" /> Uitnodiging opnieuw
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => inviteMut.mutate(r.id)}
                        disabled={inviteMut.isPending}
                        className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-violet-200 hover:bg-violet-100 disabled:opacity-50"
                        title="Klantaccount aanmaken en uitnodiging sturen"
                      >
                        <UserPlus className="size-3" /> Klant aanmaken
                      </button>
                    )}

                    {r.invite_sent_at && (
                      <span
                        className={`text-[10px] ${r.invite_status === "sent" ? "text-emerald-600" : r.invite_status === "dlq" || r.invite_status === "failed" || r.invite_status === "bounced" ? "text-rose-600" : "text-slate-500"}`}
                        title={r.invite_error ? `Fout: ${r.invite_error}` : `Status: ${r.invite_status}`}
                      >
                        ✉ {new Date(r.invite_sent_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}
                        {r.invite_error ? " ⚠" : ""}
                      </span>
                    )}

                    {r.postcode && (
                      <span className="flex items-center gap-1"><MapPin className="size-3" /> {r.postcode} {r.house_number}</span>
                    )}
                  </div>
                </td>

                <td className="p-3">
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#0b2a4a]"><Mail className="size-3" /> {r.email}</a>
                  <div className="flex items-center gap-1">
                    <a href={`tel:${r.phone}`} className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#0b2a4a]"><Phone className="size-3" /> {r.phone}</a>
                    <VoysCallButton phone={r.phone} size="xs" showLabel={false} variant="ghost" />
                  </div>
                </td>
                <td className="p-3"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs">{r.service}</span></td>
                <td className="p-3 whitespace-nowrap text-sm">
                  {r.estimate_total_cents
                    ? new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(r.estimate_total_cents / 100)
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="p-3 max-w-xs">
                  <p className="line-clamp-2 text-xs text-slate-600">{r.message ?? "—"}</p>
                </td>
                <td className="p-3">
                  <select
                    value={r.stage ?? "new"}
                    onChange={(e) => setStageFor(r.id, e.target.value)}
                    className="rounded border border-slate-200 bg-white px-2 py-1 text-xs"
                  >
                    {STAGES.filter((s) => s.id !== "all").map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Clock className="size-3" />
                    {new Date(r.created_at).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" })}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <Link
                    to="/medewerker/office/aanvragen/$id"
                    params={{ id: r.id }}
                    className="text-xs font-semibold text-[#0b2a4a] hover:underline"
                  >
                    Openen →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
